import React from 'react';
import styled from 'styled-components';
import { useForm, Controller } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { injectIntl, defineMessages, FormattedMessage } from 'react-intl';
import { Form, Input, Message, Icon, Label as BaseLabel } from 'semantic-ui-react';
import { FormLabel, Button } from '../../../components';
import { login } from '../../../actions/user';

const intlMessages = defineMessages({
    passwordRequired: {
        id: 'pages.password.form.changePassword.passwordError',
        defaultMessage: 'Enter your new password',
    },
    repeatPasswordRequired: {
        id: 'pages.password.form.changePassword.repeatPasswordError',
        defaultMessage: 'Repeat your new password',
    },
    passwordMismatch: {
        id: 'pages.password.form.changePassword.passwordDoesntMatchError',
        defaultMessage: 'Your password doesnt match',
    },
    tokenNotExists: {
        id: 'pages.password.form.changePassword.tokenNotExists',
        defaultMessage: 'User with provided token does not exists in our database.',
    },
    tokenNotAllowed: {
        id: 'pages.password.form.changePassword.tokenNotAllowed',
        defaultMessage: 'Invalid token or it has expired.',
    },
    defaultErrorMessage: {
        id: 'pages.password.form.changePassword.defaultErrorMessage',
        defaultMessage: 'Unable to change your password.',
    },
    noSalonError: {
        id: 'pages.login.form.noSalonError',
        defaultMessage: 'Your account is not assigned to any salon. Please contact your administrator.',
    },
    passwordLabel: {
        id: 'pages.password.form.changePassword.passwordLabel',
        defaultMessage: 'New password',
    },
    repeatPasswordLabel: {
        id: 'pages.password.form.changePassword.repeatPasswordLabel',
        defaultMessage: 'Repeat password',
    },
    submit: {
        id: 'pages.password.form.changePassword.submit',
        defaultMessage: 'Change Password',
    },
});

export const CHANGE_PASSWORD_QUERY = gql`
    mutation changePasswordByToken($input: ChangePasswordByTokenInput!) {
        changePasswordByToken(input: $input) {
            token
            role
            user {
                id
                ... on Administrator {
                    firstName
                    lastName
                    salon {
                        id
                        administrator {
                            id
                        }
                    }
                }
            }
        }
    }
`;

const SubmitButton = styled(Button)`
    text-transform: uppercase !important;
`;

function ChangePasswordForm({ token, intl }) {
    const dispatch = useDispatch();
    const history = useHistory();
    const [changePassword] = useMutation(CHANGE_PASSWORD_QUERY);
    const {
        control,
        handleSubmit,
        watch,
        setError,
        formState: { errors, isSubmitting, isDirty },
    } = useForm({ defaultValues: { password: '', repeatPassword: '' } });

    const onSubmit = async ({ password }) => {
        try {
            const { data } = await changePassword({
                variables: { input: { token, password } },
            });
            const { user, token: newToken, role } = data.changePasswordByToken;
            let userProps, url;
            if (role === 'SUPPLIER') {
                userProps = { id: user.id, token: newToken, role };
                url = '/supplier/seminars';
            } else if (!user.salon) {
                setError('root', {
                    message: intl.formatMessage(intlMessages.noSalonError),
                });
                return;
            } else {
                userProps = {
                    id: user.id,
                    role,
                    token: newToken,
                    salon: user.salon.id,
                    isOwner: user.id === user.salon.administrator.id,
                };
                url = '/salon/appointments';
            }
            dispatch(login(userProps));
            history.push(url);
        } catch (error) {
            if (error.graphQLErrors) {
                const graphQLError = error.graphQLErrors[0];
                switch (graphQLError.name) {
                    case 'NotExists':
                        setError('root', {
                            message: intl.formatMessage(intlMessages.tokenNotExists),
                        });
                        return;
                    case 'NotAllowed':
                        setError('root', {
                            message: intl.formatMessage(intlMessages.tokenNotAllowed),
                        });
                        return;
                    default:
                        setError('root', {
                            message: intl.formatMessage(intlMessages.defaultErrorMessage),
                        });
                        return;
                }
            }
            setError('root', {
                message: intl.formatMessage(intlMessages.defaultErrorMessage),
            });
        }
    };

    return (
        <Form onSubmit={handleSubmit(onSubmit)} error={!!errors.root}>
            {errors.root && (
                <Message error>
                    <Icon name="warning sign" />
                    {errors.root.message}
                </Message>
            )}
            <Form.Field error={!!errors.password}>
                <FormLabel>
                    <FormattedMessage {...intlMessages.passwordLabel} />
                </FormLabel>
                <Controller
                    name="password"
                    control={control}
                    rules={{ required: intl.formatMessage(intlMessages.passwordRequired) }}
                    render={({ field: { ref: _ref, ...field } }) => (
                        <Input {...field} type="password" size="big" fluid />
                    )}
                />
                {errors.password && (
                    <BaseLabel color="red" pointing basic>
                        {errors.password.message}
                    </BaseLabel>
                )}
            </Form.Field>
            <Form.Field error={!!errors.repeatPassword}>
                <FormLabel>
                    <FormattedMessage {...intlMessages.repeatPasswordLabel} />
                </FormLabel>
                <Controller
                    name="repeatPassword"
                    control={control}
                    rules={{
                        required: intl.formatMessage(intlMessages.repeatPasswordRequired),
                        validate: value =>
                            value === watch('password') ||
                            intl.formatMessage(intlMessages.passwordMismatch),
                    }}
                    render={({ field: { ref: _ref, ...field } }) => (
                        <Input {...field} type="password" size="big" fluid />
                    )}
                />
                {errors.repeatPassword && (
                    <BaseLabel color="red" pointing basic>
                        {errors.repeatPassword.message}
                    </BaseLabel>
                )}
            </Form.Field>
            <SubmitButton
                onClick={handleSubmit(onSubmit)}
                disabled={isSubmitting || !isDirty}
                loading={isSubmitting}
                primary
                fluid
            >
                <FormattedMessage {...intlMessages.submit} />
            </SubmitButton>
        </Form>
    );
}

export default injectIntl(ChangePasswordForm);
